import { Button } from '@edsn/pie-components';
import IconDelete from '@iconify/icons-material-symbols/delete-outline';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '#pie/components/icon/Icon';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import type { NotificationSubcategoryDto } from '#pie/internal/api/pie-bff';
import {
  getGetNotificationQueryKey,
  useDeleteNotificationSubCategoryId,
} from '#pie/internal/api/pie-bff';

interface RemoveNotificationButtonProps {
  data: NotificationSubcategoryDto;
}

export const RemoveNotificationButton = ({
  data,
}: RemoveNotificationButtonProps) => {
  const { addToast } = useToast();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { t: tom } = useTranslation('organisationManagement');
  const queryClient = useQueryClient();

  /* istanbul ignore next -- @preserve*/
  const { mutate, isPending: isLoading } = useDeleteNotificationSubCategoryId({
    mutation: {
      onError: (error) => {
        addToast({
          message: `${tom('toast.error.message_adjusting_email')}`,
          title: `${tom('toast.error.title.delete_notification')}`,
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        addToast({
          message: `${tom('toast.success.message_notification_deleted', {
            name: data.subcategoryName,
          })}`,
          title: `${tom('toast.success.title.delete_notification')}`,
          type: 'success',
        });
        setOpenDeleteDialog(false);
        queryClient.invalidateQueries({
          predicate: (m) => {
            return m.queryKey.some(
              (n) => typeof n === 'string' && n.includes(data.id),
            );
          },
        });
        queryClient.invalidateQueries({
          queryKey: getGetNotificationQueryKey(),
        });
        queryClient.removeQueries({
          queryKey: ['/notification'],
        });
      },
    },
  });

  return (
    <>
      <Button
        iconStart={<Icon icon={IconDelete} />}
        action="tertiary"
        emphasis="minimal"
        className="ml-auto p-0"
        aria-label={`${tom('notification.button.delete.aria-label')}${data.subcategoryName}`}
        onClick={() => setOpenDeleteDialog(true)}
      />
      <Dialog
        title={tom('title.delete_notification')}
        open={openDeleteDialog}
        onOpenChange={setOpenDeleteDialog}
      >
        <Stack gap="lg">
          <Text>
            {tom('delete.message_notification', { name: data.subcategoryName })}
          </Text>
          <div className="grid-auto grid auto-cols-fr grid-flow-col gap-2">
            <Button
              action="tertiary"
              emphasis="subtle"
              onClick={() => {
                setOpenDeleteDialog(false);
              }}
            >
              {tom('delete.button.cancel_keep_exception')}
            </Button>
            <Button
              isLoading={isLoading}
              action="danger"
              emphasis="bold"
              onClick={() => mutate({ id: data.id })}
            >
              {tom('delete.button.delete_remove_exception')}
            </Button>
          </div>
        </Stack>
      </Dialog>
    </>
  );
};
